import React, { useState, useEffect, useCallback } from "react";
import { FileManager as ReactFileManager } from "@cubone/react-file-manager";
import "@cubone/react-file-manager/dist/style.css";
import { message, Modal } from "antd";
import { apiService } from "../services/api";
import { LandingFile, LandingTemplate, BackendLandingFile } from "../types/api";
import { useAuthStore } from "../stores/authStore";

// Типи для файлового менеджера
interface FileManagerFile {
  name: string;
  isDirectory: boolean;
  path: string;
  updatedAt?: string;
  size?: number;
  // Додаткові поля для інтеграції
  id?: string;
  landingFileId?: string;
}

interface FileManagerProps {
  template: LandingTemplate;
  visible: boolean;
  onClose: () => void;
  onFileEdit?: (file: LandingFile, content: string) => void;
}

// NEW — адаптер backend → твой фронтовый интерфейс
const toFrontLandingFile = (b: BackendLandingFile): LandingFile => {
  const filePathRaw = b.path ?? b.filename ?? "";
  const filePath = filePathRaw.startsWith("/")
    ? filePathRaw
    : `/${filePathRaw}`;

  // Определяем, что это папка:
  const isDir =
    b.type === "directory" ||
    Boolean(b.metadata?.r2Key && String(b.metadata.r2Key).endsWith("/"));

  // вычисляем parentPath (без ведущего '/'), но это вторично
  let parentPath: string | undefined;
  if (filePath !== "/") {
    const i = filePath.lastIndexOf("/");
    parentPath = i > 0 ? filePath.slice(1, i) : undefined;
  }

  return {
    id: b.id,
    landingTemplateId: b.landingTemplateId,
    fileName: b.filename,
    filePath,
    fileType: b.type,
    fileSize: b.size,
    r2Key: b.metadata?.r2Key ?? "",
    isDirectory: isDir,
    parentPath,
    createdAt: (b.createdAt as any)?.toString?.() ?? String(b.createdAt),
    updatedAt: (b.updatedAt as any)?.toString?.() ?? String(b.updatedAt),
  };
};

export const FileManager: React.FC<FileManagerProps> = ({
  template,
  visible,
  onClose,
  onFileEdit,
}) => {
  const [files, setFiles] = useState<FileManagerFile[]>([]);
  const [loading, setLoading] = useState(false);
  const { token } = useAuthStore();

  // Отримуємо правильний базовий URL
  const API_BASE_URL =
    import.meta.env.VITE_API_URL ||
    (window.location.origin.includes("localhost")
      ? "http://localhost:3000"
      : "/api");

  // Мапінг LandingFile в FileManagerFile
  const mapLandingFileToManagerFile = (
    landingFile: LandingFile
  ): FileManagerFile => {
    // Забезпечуємо, щоб шлях завжди був правильно сформований
    let filePath = landingFile.filePath || landingFile.fileName;

    // Якщо це файл в корені, додаємо слеш на початок
    if (!filePath.startsWith("/")) {
      filePath = "/" + filePath;
    }

    return {
      name: landingFile.fileName,
      isDirectory: landingFile.isDirectory,
      path: filePath,
      updatedAt: landingFile.updatedAt,
      size: landingFile.fileSize,
      id: landingFile.id,
      landingFileId: landingFile.id,
    };
  };

  // Завантаження файлів
  const loadFiles = useCallback(async () => {
    if (!template) return;

    try {
      setLoading(true);
      console.log(
        "🔍 Loading ALL files for template:",
        template.id,
        "with token:",
        token ? "exists" : "missing"
      );

      // Використовуємо новий endpoint для отримання всіх файлів (включаючи вкладені)
      const landingFiles = (await apiService.getAllLandingFiles(
        template.id
      )) as unknown as BackendLandingFile[];

      const landingFilesToAdapt: LandingFile[] = (landingFiles ?? []).map(
        toFrontLandingFile
      );

      console.log(
        "📁 All landing files received:",
        landingFilesToAdapt.length,
        "total"
      );
      console.log(
        "📁 Files structure preview:",
        landingFilesToAdapt.map((f) => ({
          name: f.fileName,
          path: f.filePath,
          isDir: f.isDirectory,
          parent: f.parentPath,
        }))
      );

      // Мапимо файли
      const managerFiles = landingFilesToAdapt
        .filter((file) => file && file.fileName) // Фільтруємо null/undefined файли
        .map(mapLandingFileToManagerFile);

      console.log(
        "✅ Mapped manager files for React File Manager:",
        managerFiles.length,
        "files"
      );
      console.log(
        "📁 Final file structure:",
        managerFiles.map((f) => ({
          name: f.name,
          path: f.path,
          isDir: f.isDirectory,
        }))
      );

      setFiles(managerFiles);
    } catch (error: any) {
      console.error("❌ Error loading files:", error);
      console.error("❌ Error details:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      message.error("Помилка завантаження файлів");
    } finally {
      setLoading(false);
    }
  }, [template, token]);

  // Завантаження файлів при відкритті модалу
  useEffect(() => {
    if (visible && template) {
      loadFiles();
    }
  }, [visible, template, loadFiles]);

  // Створення папки
  const handleCreateFolder = async (
    name: string,
    parentFolder: FileManagerFile
  ) => {
    try {
      console.log("Creating folder:", name, "in parent:", parentFolder);

      // Обробляємо шлях для створення папки
      let parentPath = "";
      let dirPath = name;

      if (parentFolder && parentFolder.path && parentFolder.path !== "/") {
        // Якщо це не корінь, отримуємо шлях батьківської папки
        parentPath = parentFolder.path.startsWith("/")
          ? parentFolder.path.substring(1)
          : parentFolder.path;
        dirPath = parentPath ? `${parentPath}/${name}` : name;
      }

      console.log("API call params:", {
        templateId: template.id,
        name,
        dirPath,
        parentPath,
      });

      await apiService.createLandingDirectory(
        template.id,
        name,
        dirPath,
        parentPath || undefined
      );

      message.success(`Папку "${name}" створено`);
      await loadFiles();
    } catch (error) {
      console.error("Error creating folder:", error);
      message.error("Помилка створення папки");
    }
  };

  // Завантаження файлу
  const handleFileUploading = (
    uploadingFile: FileManagerFile,
    parentFolder: FileManagerFile
  ) => {
    console.log(
      "File uploading:",
      uploadingFile,
      "parent folder:",
      parentFolder
    );

    // Обробляємо шлях для завантаження
    let parentPath = "";

    if (parentFolder && parentFolder.path && parentFolder.path !== "/") {
      // Якщо це не корінь, отримуємо шлях без початкового слешу
      parentPath = parentFolder.path.startsWith("/")
        ? parentFolder.path.substring(1)
        : parentFolder.path;
    }

    console.log("File uploading to path:", parentPath || "root");

    // Повертаємо додаткові дані для FormData
    return {
      templateId: template.id,
      parentPath: parentPath || undefined,
      // Не передаємо filePath - він буде згенерований автоматично на бекенді
    };
  };

  // Після успішного завантаження файлу
  const handleFileUploaded = async (_response: any) => {
    try {
      message.success("Файл завантажено");
      await loadFiles();
    } catch (error) {
      message.error("Помилка оновлення списку файлів");
    }
  };

  // Видалення файлів
  const handleDelete = async (filesToDelete: FileManagerFile[]) => {
    try {
      for (const file of filesToDelete) {
        if (file.landingFileId) {
          await apiService.deleteLandingFile(file.landingFileId);
        }
      }
      message.success("Файли видалено");
      await loadFiles();
    } catch (error) {
      message.error("Помилка видалення файлів");
    }
  };

  // Перейменування файлу/папки
  const handleRename = async (file: FileManagerFile, newName: string) => {
    try {
      console.log("Renaming file:", file, "to:", newName);

      if (!file.landingFileId) {
        message.error("Неможливо перейменувати: відсутній ID файлу");
        return;
      }

      await apiService.renameLandingFile(file.landingFileId, newName);
      message.success(`Файл перейменовано на "${newName}"`);
      await loadFiles();
    } catch (error) {
      console.error("Error renaming file:", error);
      message.error("Помилка перейменування");
    }
  };

  // Копіювання/переміщення файлів
  const handlePaste = async (
    filesToPaste: FileManagerFile[],
    destinationFolder: FileManagerFile,
    operationType: "copy" | "move"
  ) => {
		try {
			if (filesToPaste.some(f => f.isDirectory)) {
				message.warning("Вставка директорій заборонена");
        return false;
			}
				
      console.log(
        "Pasting files:",
        filesToPaste,
        "to:",
        destinationFolder,
        "operation:",
        operationType
      );

      // Отримуємо ID файлів для операції
      const fileIds = filesToPaste
        .filter((f) => f.landingFileId)
        .map((f) => f.landingFileId!);

      if (fileIds.length === 0) {
        message.error("Немає файлів для операції");
        return;
      }

      // Формуємо шлях призначення
      let destinationPath = "";
      if (
        destinationFolder &&
        destinationFolder.path &&
        destinationFolder.path !== "/"
      ) {
        destinationPath = destinationFolder.path.startsWith("/")
          ? destinationFolder.path.substring(1)
          : destinationFolder.path;
      }

      console.log("API call params:", {
        fileIds,
        destinationPath,
        templateId: template.id,
      });

      if (operationType === "copy") {
        await apiService.copyLandingFiles(
          fileIds,
          destinationPath,
          template.id
        );
        message.success(`Файли скопійовано (${fileIds.length})`);
      } else {
        await apiService.moveLandingFiles(
          fileIds,
          destinationPath,
          template.id
        );
        message.success(`Файли переміщено (${fileIds.length})`);
      }

      await loadFiles();
    } catch (error) {
      console.error(`Error ${operationType} files:`, error);
      message.error(
        `Помилка ${operationType === "copy" ? "копіювання" : "переміщення"}`
      );
    }
  };

  // Оновлення списку файлів
  const handleRefresh = () => {
    loadFiles();
  };

  // Завантаження файлів
  const handleDownload = async (filesToDownload: FileManagerFile[]) => {
    for (const file of filesToDownload) {
      if (!file.isDirectory && file.landingFileId) {
        try {
          const blob = await apiService.getLandingFileContent(
            file.landingFileId
          );
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
        } catch (error) {
          message.error(`Помилка завантаження файлу ${file.name}`);
        }
      }
    }
  };

  // Обробка помилок
  const handleError = (
    error: { type: string; message: string },
    _file: FileManagerFile
  ) => {
    message.error(`${error.type}: ${error.message}`);
  };

  // Відкриття файлу для редагування
  const handleFileOpen = async (file: FileManagerFile) => {
    console.log("🔍 Opening file:", file);

    // ВАЖЛИВО: Зупиняємо будь-яку навігацію або переходи

    // Тільки для не-директорій і тільки якщо є callback для редагування
    if (!file.isDirectory && file.landingFileId && onFileEdit) {
      try {
        console.log("📝 Loading file content for editing:", file.name);

        const blob = await apiService.getLandingFileContent(file.landingFileId);
        const content = await blob.text();

        // Знаходимо оригінальний LandingFile (використовуємо новий метод)
        const landingFilesRaw = (await apiService.getAllLandingFiles(
          template.id
        )) as unknown as BackendLandingFile[];
        const backendFile = (landingFilesRaw ?? []).find(
          (f) => f.id === file.landingFileId
        );

        if (backendFile) {
          const landingFileFront = toFrontLandingFile(backendFile);
          console.log("✅ Opening file in editor:", landingFileFront.fileName);
          onFileEdit(landingFileFront, content);
        } else {
          console.error("❌ LandingFile not found for editing");
          message.error("Файл не знайдено для редагування");
        }
      } catch (error) {
        console.error("❌ Error loading file for editing:", error);
        message.error("Помилка завантаження файлу для редагування");
      }
    } else {
      console.log("⚠️ File open ignored - no edit callback or is directory");
    }

    // Повертаємо false щоб запобігти будь-яким стандартним діям
    return false;
  };

  // Копіювання файлів до буфера
  const handleCopy = (filesToCopy: FileManagerFile[]) => {
    const hasDirectory = filesToCopy.some((file) => file.isDirectory);
    if (hasDirectory) {
      message.warning("Копіювання директорій заборонено");
      return false;
    }
    console.log("Files copied to clipboard:", filesToCopy);
		message.info(`Скопійовано файлів: ${filesToCopy.length}`);
		return true
  };

  // Вирізання файлів до буфера
  const handleCut = (filesToCut: FileManagerFile[]) => {
    const hasDirectory = filesToCut.some((file) => file.isDirectory);
    if (hasDirectory) {
      message.warning("Вирізання директорій заборонено");
      return false;
    }
    console.log("Files cut to clipboard:", filesToCut);
		message.info(`Вирізано файлів: ${filesToCut.length}`);
		return true 
  };

  return (
    <Modal
      title={`Файловий менеджер - ${template.name}`}
      open={visible}
      onCancel={onClose}
      width="95vw"
      style={{ top: 20 }}
      styles={{
        body: {
          height: "calc(100vh - 150px)",
          padding: 0,
        },
      }}
      footer={null}
      destroyOnClose
    >
      <ReactFileManager
        files={files}
        height="100%"
        width="100%"
        isLoading={loading}
        primaryColor="#1890ff"
        fontFamily="'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif"
        permissions={{
          create: true,
          upload: true,
          move: !template?.active,
          copy: !template?.active,
          rename: !template?.active,
          download: true,
          delete: true,
        }}
        fileUploadConfig={{
          url: `${API_BASE_URL}/landings/templates/${template.id}/files`,
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }}
        maxFileSize={50 * 1024 * 1024} // 50MB
        acceptedFileTypes="image/*,.png,.jpg,.jpeg,.gif,.svg,.webp,.ico,.ttf,.woff,.woff2,.eot,.txt,.html,.css,.js,.json,.md,.xml,.yml,.yaml,.env,.gitignore,.htaccess,.php,.py,.rb,.java,.c,.cpp,.h,.hpp,.cs,.vb,.sql,.sh,.bat,.ps1,.dockerfile,.makefile,.config,.conf,.ini,.properties,.log"
        // Callbacks для обробки дій
        onFileOpen={handleFileOpen}
        onFileUploading={handleFileUploading}
        onFileUploaded={handleFileUploaded}
        onCreateFolder={handleCreateFolder}
        onDelete={handleDelete}
        onRename={handleRename}
        onPaste={handlePaste}
        onRefresh={handleRefresh}
        onDownload={handleDownload}
        onError={handleError}
        onCopy={handleCopy}
        onCut={handleCut}
        // Налаштування для контролю поведінки
        enableFilePreview={false}
        collapsibleNav={true}
        defaultNavExpanded={true}
        layout="grid"
      />
    </Modal>
  );
};
